import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Send, Paperclip, X, ArrowLeft, Search } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";

// Harus sama dengan TUJUAN_TIKET_PER_ROLE di backend; role lain bebas memilih tujuan.
const TUJUAN_PER_ROLE = {
  SPPG: ["KPPG", "Pemda", "Pemprov", "Supplier"],
  Supplier: ["KPPG", "Pemda", "Pemprov", "SPPG"],
};

const MAX_BYTES = 5 * 1024 * 1024;
const MAX_FILES = 3;

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = reject;
    r.onload = () => {
      const result = r.result || "";
      // strip data:...;base64, prefix
      const idx = String(result).indexOf("base64,");
      resolve(idx >= 0 ? String(result).slice(idx + 7) : String(result));
    };
    r.readAsDataURL(file);
  });
}

export default function BuatTicketPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    assignee_id: "", subject: "", description: "", priority: "Sedang",
  });
  const [files, setFiles] = useState([]); // {name, mime, size, data_base64}
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Search box state
  const [searchQuery, setSearchQuery] = useState("");
  const { user } = useAuth();
  const tujuanTerbatas = TUJUAN_PER_ROLE[user?.role];
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/tickets/assignable-users");
      setUsers(data);
    })();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredUsers = users.filter((u) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      u.name.toLowerCase().includes(q) ||
      u.role.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q) ||
      (u.organization || "").toLowerCase().includes(q)
    );
  });

  const selectUser = useCallback((u) => {
    setForm((prev) => ({ ...prev, assignee_id: u.id }));
    setSearchQuery(u.name);
    setShowDropdown(false);
  }, []);

  const clearSelection = useCallback(() => {
    setForm((prev) => ({ ...prev, assignee_id: "" }));
    setSearchQuery("");
    setShowDropdown(false);
  }, []);

  const onFiles = async (e) => {
    setError("");
    const fl = Array.from(e.target.files || []);
    const remaining = MAX_FILES - files.length;
    if (fl.length > remaining) {
      setError(`Maksimal ${MAX_FILES} file per tiket`);
      return;
    }
    const acc = [];
    for (const f of fl) {
      if (f.size > MAX_BYTES) {
        setError(`File "${f.name}" melebihi 5 MB`);
        return;
      }
      const b64 = await readFileAsBase64(f);
      acc.push({ name: f.name, mime: f.type || "application/octet-stream", size: f.size, data_base64: b64 });
    }
    setFiles((s) => [...s, ...acc]);
    e.target.value = "";
  };

  const removeFile = (i) => setFiles((s) => s.filter((_, j) => j !== i));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.assignee_id) { setError("Pilih user tujuan"); return; }
    if (!form.subject.trim()) { setError("Isi subjek tiket"); return; }
    if (!form.description.trim()) { setError("Isi deskripsi kendala"); return; }
    setSubmitting(true);
    try {
      const payload = { ...form, attachments: files.length ? files : null };
      const { data } = await api.post("/tickets", payload);
      navigate(`/tickets/${data.id}`);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  const ROLE_COLOR = {
    KPPG: "bg-blue-50 text-blue-700",
    SPPG: "bg-amber-50 text-amber-700",
    Supplier: "bg-emerald-50 text-emerald-700",
    TPID: "bg-violet-50 text-violet-700",
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="buat-ticket-page">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> Kembali
      </button>

      <header>
        <div className="kedan-label text-slate-500">Bantuan</div>
        <h1 className="text-3xl font-display font-bold text-slate-900 mt-1">Buat Laporan Kendala</h1>
        <p className="text-slate-500 text-sm mt-1">Sampaikan keluhan/permintaan ke user tertentu. SLA dihitung otomatis berdasarkan prioritas.</p>
      </header>

      <form onSubmit={submit} className="kedan-card p-6 space-y-5">
        {/* Assignee search box */}
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1.5 block">User Tujuan *</label>
          {tujuanTerbatas && (
            <p className="text-xs text-slate-500 -mt-1 mb-1.5" data-testid="assignee-hint">
              Sebagai {user.role}, tiket dapat ditujukan ke: {tujuanTerbatas.join(", ")}.
            </p>
          )}
          <div className="relative" ref={searchRef}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                type="text"
                className="kedan-input !pl-9 !pr-9"
                placeholder="Cari nama, role, atau email user..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowDropdown(true);
                  if (form.assignee_id) {
                    setForm((prev) => ({ ...prev, assignee_id: "" }));
                  }
                }}
                onFocus={() => setShowDropdown(true)}
                data-testid="form-assignee"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={clearSelection}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="clear-assignee"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {showDropdown && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {filteredUsers.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-slate-500">Tidak ada user ditemukan</div>
                ) : (
                  filteredUsers.map((u) => (
                    <button
                      type="button"
                      key={u.id}
                      onClick={() => selectUser(u)}
                      className={`w-full text-left px-4 py-2.5 hover:bg-slate-50 flex items-center gap-3 transition-colors border-b border-slate-100 last:border-b-0 ${form.assignee_id === u.id ? "bg-blue-50" : ""
                        }`}
                      data-testid={`assignee-option-${u.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">{u.name}</div>
                        <div className="text-xs text-slate-500 truncate">{u.email} · {u.organization || "-"}</div>
                      </div>
                      <span className={`kedan-badge shrink-0 ${ROLE_COLOR[u.role]}`}>{u.role}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {form.assignee_id && (() => {
            const u = users.find((x) => x.id === form.assignee_id);
            return u ? (
              <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 bg-slate-50 rounded-md border border-slate-200">
                <span className={`kedan-badge ${ROLE_COLOR[u.role]}`}>{u.role}</span>
                <span className="text-xs text-slate-600">{u.organization || "-"}</span>
              </div>
            ) : null;
          })()}
        </div>

        {/* Priority */}
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1.5 block">Prioritas *</label>
          <div className="grid grid-cols-3 gap-2">
            {[
              { v: "Rendah", sla: "168 jam", cls: "border-slate-300", active: "bg-slate-100 border-slate-500" },
              { v: "Sedang", sla: "72 jam", cls: "border-amber-300", active: "bg-amber-50 border-amber-500" },
              { v: "Tinggi", sla: "24 jam", cls: "border-red-300", active: "bg-red-50 border-red-500" },
            ].map((p) => (
              <button type="button" key={p.v} onClick={() => setForm({ ...form, priority: p.v })}
                className={`p-3 rounded-md border-2 text-left transition-all ${form.priority === p.v ? p.active + " ring-2 ring-[#045498]/15" : `${p.cls} bg-white hover:bg-slate-50`
                  }`}
                data-testid={`priority-${p.v}`}>
                <div className="font-semibold text-slate-900 text-sm">{p.v}</div>
                <div className="text-xs text-slate-500 mt-0.5">SLA: {p.sla}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Subject */}
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1.5 block">Subjek *</label>
          <input className="kedan-input" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
            placeholder="Ringkas kendala dalam 1 kalimat..." data-testid="form-subject" maxLength={140} />
        </div>

        {/* Description */}
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1.5 block">Deskripsi Kendala *</label>
          <textarea className="kedan-input" rows={5} required value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Jelaskan detail kendala, lokasi, waktu kejadian, dampak, dll." data-testid="form-description" />
        </div>

        {/* Attachments */}
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1.5 block">
            Lampiran (opsional, maks {MAX_FILES} file × 5 MB)
          </label>
          <label className="inline-flex items-center gap-2 px-3 py-2 border border-dashed border-slate-300 rounded-md cursor-pointer hover:bg-slate-50 text-sm text-slate-600 w-full" data-testid="file-picker-label">
            <Paperclip className="w-4 h-4" />
            <span>Pilih file (gambar/PDF/dokumen)</span>
            <input type="file" multiple onChange={onFiles} className="hidden" accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx" data-testid="file-input" />
          </label>
          {files.length > 0 && (
            <div className="mt-3 space-y-2" data-testid="file-list">
              {files.map((f, i) => (
                <div key={i} className="flex items-center justify-between bg-slate-50 px-3 py-2 rounded-md border border-slate-200">
                  <div className="flex items-center gap-2 text-sm truncate">
                    <Paperclip className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    <span className="truncate">{f.name}</span>
                    <span className="text-xs text-slate-500 shrink-0">({(f.size / 1024).toFixed(1)} KB)</span>
                  </div>
                  <button type="button" onClick={() => removeFile(i)} className="text-red-500 hover:text-red-700" data-testid={`remove-file-${i}`}>
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-3 py-2" data-testid="form-error">{error}</div>}

        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
          <button type="button" onClick={() => navigate(-1)} className="kedan-btn-secondary">Batal</button>
          <button type="submit" disabled={submitting} className="kedan-btn-primary" data-testid="submit-ticket-btn">
            <Send className="w-4 h-4" /> {submitting ? "Mengirim..." : "Kirim Tiket"}
          </button>
        </div>
      </form>
    </div>
  );
}
